import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

const STATUS_LABEL = { ALLOWED: "Allowed", BLOCKED: "Blocked", ESCALATED: "Escalated", APPROVED: "Approved (co-signed)" };

export default function EvidencePrint() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    document.body.classList.add("fc-print-mode");
    const load = async () => {
      try {
        if (token) {
          const snap = await api.getSnapshot(token);
          setData({
            scenario: snap.scenario,
            roles: snap.roles,
            receipts: snap.receipts,
            enabled: snap.enabled,
            rwi_weights: snap.rwi_weights,
            rwi_runs: snap.rwi_runs,
            sifty_signals: snap.sifty_signals,
            captured_at: snap.created_at,
            token: snap.token,
            summary: snap.summary,
            note: snap.note,
            note_lines: snap.note_lines || [],
          });
        } else {
          const sc = await api.scenario();
          const ev = await api.evidence();
          let sift = null;
          try { sift = await api.sift(); } catch { /* ignore */ }
          setData({
            scenario: sc.scenario,
            roles: sc.roles,
            receipts: ev.receipts || [],
            enabled: { sifty: params.get("sifty") !== "false", rwi: false },
            sifty_signals: sift,
            captured_at: new Date().toISOString(),
            token: null,
          });
        }
      } catch (e) {
        setError("Could not load evidence pack.");
      }
    };
    load();
    return () => document.body.classList.remove("fc-print-mode");
  }, [token, params]);

  useEffect(() => {
    if (data) {
      // Allow paint, then trigger the print dialog
      const t = setTimeout(() => window.print(), 600);
      return () => clearTimeout(t);
    }
  }, [data]);

  if (error) return <div className="p-12">{error}</div>;
  if (!data) return <div className="p-12">Preparing evidence pack…</div>;

  const s = data.scenario;
  const counts = data.summary?.outcomes || data.receipts.reduce((m, r) => ({ ...m, [r.status]: (m[r.status] || 0) + 1 }), {});
  const approved = data.receipts.filter(r => r.status === "APPROVED" || r.status === "ALLOWED");

  return (
    <div className="fc-print-page" data-testid="evidence-print-root">
      {/* Cover */}
      <section className="print-section print-cover">
        <div className="print-eyebrow">FreightCierge · Evidence Pack</div>
        <h1 className="print-title">FreightCierge Evidence Pack</h1>
        <h2 className="print-subtitle">Governed Exception Handling Snapshot</h2>
        {data.note_lines && data.note_lines.length > 0 && (
          <div className="print-host-note" data-testid="print-host-note">
            <div className="print-h4" style={{ marginTop: 0 }}>Note from host</div>
            {data.note_lines.map((line, i) => (
              <div key={i} className={i === 0 ? "print-host-note-lead" : "print-host-note-line"}>{line}</div>
            ))}
          </div>
        )}
        <div className="print-cover-meta">
          <div><span className="print-label">Scenario</span><span className="print-value">{s.title}</span></div>
          <div><span className="print-label">Corridor</span><span className="print-value">{s.corridor}</span></div>
          <div><span className="print-label">Vessel</span><span className="print-value">{s.vessel.name} ({s.vessel.voyage})</span></div>
          <div><span className="print-label">Captured</span><span className="print-value">{new Date(data.captured_at).toUTCString()}</span></div>
          {data.token && <div><span className="print-label">Snapshot token</span><span className="print-value mono">{data.token}</span></div>}
        </div>
        <p className="print-summary">
          A multi-party freight disruption was handled through the FreightCierge governance layer.
          The KATLAS node applied runtime authority and CAR governance to {data.receipts.length} consequential actions.
          This pack is an attributable, business-friendly assurance artefact.
        </p>
        <div className="print-foot-rule"/>
      </section>

      {/* Scenario overview */}
      <section className="print-section">
        <h3 className="print-h3">1 · Scenario overview</h3>
        <p className="print-p">
          <strong>{s.vessel.name}</strong> reported an ETA slip of <strong>{s.vessel.delay_hours} hours</strong> due to {s.vessel.weather.toLowerCase()}.
          {" "}<strong>{s.terminal.name}</strong> was already at <strong>{s.terminal.yard_capacity_pct}%</strong> yard capacity.
          {" "}{s.containers.filter(c => c.risk > 0.6).length} of {s.containers.length} containers were flagged at risk, including a regulated pharma cut-off.
        </p>
        <table className="print-table">
          <thead><tr><th>Container</th><th>Commodity</th><th>Priority</th><th>Commitment</th><th>Doc status</th><th>Risk</th></tr></thead>
          <tbody>
            {s.containers.map(c => (
              <tr key={c.id}>
                <td className="mono">{c.id}</td><td>{c.commodity}</td><td>{c.priority}</td>
                <td>{c.commitment}</td><td>{c.doc_status}</td><td className="mono">{Math.round(c.risk * 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="print-h4">Roles involved</div>
        <ul className="print-ul-grid">
          {data.roles.map(r => <li key={r.id}><strong>{r.label}</strong><span> — {r.org}</span></li>)}
        </ul>
      </section>

      {/* Recommended actions */}
      <section className="print-section">
        <h3 className="print-h3">2 · Recommended actions</h3>
        <p className="print-p">
          Candidate actions were surfaced by the signal layer. {data.enabled?.sifty ? "SiftyML predictive signals were enabled as a supporting input." : "The SiftyML predictive layer was not enabled in this run."}
        </p>
        <table className="print-table">
          <thead><tr><th>Action</th><th>Recommended by</th><th>Permitted role</th><th>Declared outcome</th></tr></thead>
          <tbody>
            {s.actions.map(a => {
              const role = data.roles.find(r => r.id === a.permitted_role);
              return (
                <tr key={a.id}>
                  <td>{a.label}</td><td>{a.recommended_by}</td>
                  <td>{role?.label}</td>
                  <td><span className={`print-pill print-pill-${a.outcome.toLowerCase()}`}>{STATUS_LABEL[a.outcome]}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {/* Governance decisions */}
      <section className="print-section">
        <h3 className="print-h3">3 · Governance decisions</h3>
        <p className="print-p">
          Each action was evaluated by the KATLAS node against CAR (Custody, Authority, Receipts) rules.
          Runtime outcomes recorded for this snapshot:
        </p>
        <div className="print-counts">
          <div><span className="print-count print-pill-allowed">{counts.ALLOWED || 0}</span> Allowed</div>
          <div><span className="print-count print-pill-blocked">{counts.BLOCKED || 0}</span> Blocked</div>
          <div><span className="print-count print-pill-escalated">{counts.ESCALATED || 0}</span> Escalated</div>
          <div><span className="print-count print-pill-approved">{counts.APPROVED || 0}</span> Approved (co-signed)</div>
        </div>

        {data.receipts.length === 0 ? (
          <p className="print-p print-muted">No actions were run before this snapshot was captured.</p>
        ) : data.receipts.map(r => (
          <div className="print-receipt" key={r.receipt_id}>
            <div className="print-receipt-head">
              <span className={`print-pill print-pill-${r.status.toLowerCase()}`}>{STATUS_LABEL[r.status]}</span>
              <span className="mono print-id">{r.receipt_id}</span>
            </div>
            <div className="print-receipt-title">{r.action_label}</div>
            <div className="print-receipt-meta">
              <strong>Requested by:</strong> {r.actor_role} ({r.actor_org})
            </div>
            <div className="print-receipt-meta"><strong>Authority basis:</strong> {r.authority_used}</div>
            <div className="print-receipt-meta"><strong>Reason / decision basis:</strong> {r.decision_basis}</div>
            <div className="print-receipt-meta">
              <strong>Human approval required:</strong> {r.status === "APPROVED" ? "Yes — co-signed by Compliance / Release Officer." : (r.status === "ESCALATED" ? "Yes — pending at snapshot time." : "No.")}
            </div>
            <div className="print-receipt-meta"><strong>Linked event:</strong> <span className="mono">{r.linked_event_id}</span></div>
            <div className="print-receipt-meta"><strong>Downstream notifications:</strong> {r.downstream.join(" · ")}</div>
            <div className="print-receipt-meta print-muted mono">{new Date(r.timestamp).toUTCString()}</div>
          </div>
        ))}
      </section>

      {/* Approved operational actions */}
      <section className="print-section">
        <h3 className="print-h3">4 · Approved operational actions</h3>
        <p className="print-p">What was actually permitted to happen, by whom, and what changed operationally.</p>
        {approved.length === 0 ? (
          <p className="print-p print-muted">No operational actions were approved in this snapshot.</p>
        ) : (
          <table className="print-table">
            <thead><tr><th>Action</th><th>Approved/executed by</th><th>Operational change</th></tr></thead>
            <tbody>
              {approved.map(r => (
                <tr key={r.receipt_id}>
                  <td>{r.action_label}</td>
                  <td>{r.actor_role}{r.approvals?.length ? ` · co-signed by ${r.approvals[0].approver_role}` : ""}</td>
                  <td>{r.downstream.join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Receipt summary */}
      <section className="print-section">
        <h3 className="print-h3">5 · Receipt summary</h3>
        <table className="print-table">
          <thead><tr><th>Receipt ID</th><th>Timestamp</th><th>Actor / role</th><th>Action</th><th>Authority basis</th><th>Linked event</th></tr></thead>
          <tbody>
            {data.receipts.map(r => (
              <tr key={r.receipt_id}>
                <td className="mono">{r.receipt_id}</td>
                <td className="mono">{new Date(r.timestamp).toISOString().replace("T", " ").slice(0, 19)}Z</td>
                <td>{r.actor_role}</td>
                <td>{r.action_label}</td>
                <td>{r.authority_used}</td>
                <td className="mono">{r.linked_event_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {/* Evidence summary */}
      <section className="print-section">
        <h3 className="print-h3">6 · Evidence summary</h3>
        <p className="print-p">
          During this exception, {data.roles.length} roles operated through their own purpose-bound views.
          {" "}{counts.ALLOWED || 0} action{(counts.ALLOWED || 0) === 1 ? " was" : "s were"} allowed within established decision rights.
          {" "}{counts.BLOCKED || 0} action{(counts.BLOCKED || 0) === 1 ? " was" : "s were"} blocked because the requesting role did not hold the required authority.
          {" "}{counts.ESCALATED || 0} action{(counts.ESCALATED || 0) === 1 ? " was" : "s were"} escalated for named approval; {counts.APPROVED || 0} {(counts.APPROVED || 0) === 1 ? "was" : "were"} subsequently co-signed by the Compliance / Release Officer.
          {" "}Every consequential outcome produced an attributable receipt referencing the upstream event and downstream notifications.
        </p>
      </section>

      {/* Business outcome */}
      <section className="print-section">
        <h3 className="print-h3">7 · Business outcome</h3>
        <ul className="print-ul">
          <li><strong>Reduced ambiguity</strong> — every role saw a purpose-bound view of the same event.</li>
          <li><strong>Clearer accountability</strong> — every action attributed to a named role and organisation.</li>
          <li><strong>Faster exception resolution</strong> — recommendations were routed to the role that may decide.</li>
          <li><strong>Stronger audit readiness</strong> — the evidence pack assembled itself as the response unfolded.</li>
        </ul>
      </section>

      {/* Appendix */}
      <section className="print-section">
        <h3 className="print-h3">Appendix</h3>
        <div className="print-h4">Enabled scenario settings</div>
        <ul className="print-ul">
          <li>SiftyML predictive signals: {data.enabled?.sifty ? "enabled" : "disabled"}</li>
          <li>RWI scenario controls: {data.enabled?.rwi ? "enabled" : "disabled"}</li>
        </ul>
        {data.enabled?.rwi && data.rwi_weights && (
          <>
            <div className="print-h4">RWI scenario values</div>
            <table className="print-table">
              <thead><tr><th>Pressure</th><th>Value</th></tr></thead>
              <tbody>
                {Object.entries(data.rwi_weights).map(([k, v]) => (
                  <tr key={k}><td style={{ textTransform: "capitalize" }}>{k}</td><td className="mono">{v}</td></tr>
                ))}
              </tbody>
            </table>
            {data.rwi_runs && (
              <table className="print-table">
                <thead><tr><th>Metric</th><th>Unguided</th><th>Governed</th></tr></thead>
                <tbody>
                  <tr><td>Resolution (minutes)</td><td className="mono">{data.rwi_runs.unguided.resolution_minutes}</td><td className="mono">{data.rwi_runs.governed.resolution_minutes}</td></tr>
                  <tr><td>Unauthorised actions</td><td className="mono">{data.rwi_runs.unguided.unauthorised_actions}</td><td className="mono">{data.rwi_runs.governed.unauthorised_actions}</td></tr>
                  <tr><td>Decision confidence</td><td className="mono">{data.rwi_runs.unguided.decision_confidence}%</td><td className="mono">{data.rwi_runs.governed.decision_confidence}%</td></tr>
                  <tr><td>Evidence completeness</td><td className="mono">{data.rwi_runs.unguided.evidence_completeness}%</td><td className="mono">{data.rwi_runs.governed.evidence_completeness}%</td></tr>
                </tbody>
              </table>
            )}
          </>
        )}
        <div className="print-h4">JSON evidence reference</div>
        <p className="print-p print-muted mono">
          {data.token
            ? `snapshot:${data.token} · receipts:${data.receipts.length} · captured:${data.captured_at}`
            : `live:export · receipts:${data.receipts.length} · captured:${data.captured_at}`}
        </p>
      </section>

      <div className="fc-no-print" style={{ position: "fixed", bottom: 24, right: 24 }}>
        <button onClick={() => window.print()} className="bg-[var(--navy)] text-white rounded-sm px-4 py-2 text-[12px]">
          Print / Save as PDF
        </button>
      </div>
    </div>
  );
}
